
import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional role that is required to view the wrapped component */
  requiredRole?: 'manager' | 'partner';
}

/**
 * Guard component that protects routes based on authentication state
 * and optional role requirements.
 *
 * It handles three main scenarios:
 * 1. While the auth information is loading – shows a spinner.
 * 2. When there is no authenticated user – redirects to the login page.
 * 3. When a user is authenticated but lacks the required role – shows an
 *    access‑denied message (or redirects for partners to an allowed page).
 *
 * The function is deliberately kept simple and type‑safe.  Any unexpected
 * runtime errors are caught and displayed as a fallback UI.
 */
export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps) {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();

  // 1️⃣ Loading state – show a centered spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // 2️⃣ No user – redirect to the login page
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 3️⃣ User exists but profile is still being fetched
  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Đang tải thông tin người dùng...
          </h2>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  // 4️⃣ Partner‑specific guard – restrict navigation to allowed sections
  if (userProfile.role === 'partner') {
    const allowedPaths = ['/products', '/orders', '/reports'];
    const currentPath = location.pathname;

    const isAllowed = allowedPaths.some((path) => currentPath.startsWith(path));

    if (!isAllowed) {
      // Partners are forced to the products page if they try to access something else
      return <Navigate to="/products" replace />;
    }
  }

  // 5️⃣ Generic role guard – display an explicit “access denied” UI
  if (requiredRole && userProfile.role !== requiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Không có quyền truy cập
          </h2>
          <p className="text-gray-600">
            Bạn không có quyền truy cập vào trang này.
          </p>
        </div>
      </div>
    );
  }

  // ✅ All checks passed – render the protected children
  return <>{children}</>;
}
